import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { FaKey, FaMobileAlt, FaPlus, FaSync, FaLifeRing } from 'react-icons/fa'
import { Box, Stack, FormControl, Input, Heading, Text, Button, SimplePopover, SimpleModal, Alert, Icon, SimpleGrid, Card, Badge } from '@/components/lib'
import AuthenticatorSetup from '@/components/security/AuthenticatorSetup'
import PasskeyRegistration from '@/components/security/PasskeyRegistration'
import RecoveryCodes from '@/components/security/RecoveryCodes'
import utils from '../../utils/utils'
import api from '../../api'
import useStore from '../../store'

function AccountPassword () {
  const [existingPassword, setExistingPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [disableOtpPassword, setDisableOtpPassword] = useState('')
  const [passkeys, setPasskeys] = useState([])
  const [deletePasskeyPassword, setDeletePasskeyPassword] = useState('')
  const [renamePasskeyName, setRenamePasskeyName] = useState('')
  const [recoveryCodesPassword, setRecoveryCodesPassword] = useState('')
  const [newRecoveryCodes, setNewRecoveryCodes] = useState(null)
  const [searchParams] = useSearchParams()
  // Sent here straight after signing in with a recovery code.
  const justRecovered = searchParams.get('recovered') === '1'

  const { user, updateUser } = useStore()
  const hasMfa = !!(user?.otpEnabled || user?.hasPasskey)
  const recovery = user?.recoveryCodesStatus || { remaining: 0, used: 0, generatedAt: null }
  const recoveryNeedsAttention = hasMfa && (!recovery.generatedAt || recovery.used > 0 || recovery.remaining <= 2)

  // An account with a password has to keep at least one second factor, so
  // the last authenticator or passkey can't be removed - only replaced.
  const factorCount = (user?.otpEnabled ? 1 : 0) + passkeys.length
  const isLastFactor = factorCount <= 1

  const refreshPasskeys = () => {
    api.accounts.listPasskeys(data => setPasskeys(data.passkeys || []), () => {})
  }

  useEffect(() => {
    if (user?.hasPassword) refreshPasskeys()
  }, [user?.hasPassword])

  const updatePassword = () => {
    api.accounts.updatePassword(existingPassword, newPassword, () => {
      setExistingPassword('')
      setNewPassword('')
      utils.toast('success', 'Password updated')
    }, err => utils.toast('error', 'Unable to update your password', err.message))
  }

  const onAuthenticatorSetUp = () => {
    updateUser(user._id, { otpEnabled: true, mfaEnrolRequired: false })
    utils.toast('success', user?.otpEnabled ? 'Authenticator app replaced' : 'Authenticator app set up')
  }
  const disableOtp = () => {
    api.accounts.disableOtp(disableOtpPassword, data => {
      updateUser(user._id, { otpEnabled: false })
    }, err => utils.toast('error', err.message))
  }

  const onPasskeyAdded = () => {
    updateUser(user._id, { hasPasskey: true, mfaEnrolRequired: false })
    refreshPasskeys()
    utils.toast('success', 'Passkey added')
  }

  const renamePasskey = (passkeyId) => {
    api.accounts.renamePasskey(passkeyId, renamePasskeyName, data => {
      // The popover stays open, so keep the saved name in the field rather
      // than blanking it: a second Save would otherwise send "" and fail.
      setRenamePasskeyName(data.passkey.name)
      setPasskeys(passkeys.map(p => p._id === passkeyId ? { ...p, name: data.passkey.name } : p))
      utils.toast('success', 'Passkey renamed')
    }, err => utils.toast('error', 'Unable to rename your passkey', err.message))
  }

  const regenerateRecoveryCodes = () => {
    api.accounts.regenerateRecoveryCodes(recoveryCodesPassword, data => {
      setRecoveryCodesPassword('')
      setNewRecoveryCodes(data.recoveryCodes)
      updateUser(user._id, { recoveryCodesStatus: { remaining: data.recoveryCodes.length, used: 0, generatedAt: new Date().toISOString() } })
    }, err => utils.toast('error', 'Unable to generate recovery codes', err.message))
  }

  const removePasskey = (passkeyId) => {
    api.accounts.deletePasskey(passkeyId, deletePasskeyPassword, data => {
      setDeletePasskeyPassword('')
      const remaining = passkeys.filter(p => p._id !== passkeyId)
      setPasskeys(remaining)
      if (remaining.length === 0) updateUser(user._id, { hasPasskey: false })
      utils.toast('success', 'Passkey removed')
    }, err => utils.toast('error', 'Unable to remove your passkey', err.message))
  }

  return (
    <Stack gap={5}>
      <Stack rounded='lg' borderWidth='1px' p={3} gap={3}>
        <Box>
          <Stack direction='row' alignItems='center'>
            <Heading size='md' as='h3'>Change account password</Heading>
            <Badge colorPalette='blue'>Login</Badge>
          </Stack>
          <Text>Update your account password to one that is strong and hard to guess.</Text>
        </Box>

        {user?.hasPassword
          ? (
            <Stack>
              <FormControl label='Current password'>
                <Input type='password' value={existingPassword} onChange={e => setExistingPassword(e.target.value)} />
              </FormControl>

              <FormControl label='New password'>
                <Input label='New password' type='password' value={newPassword} onChange={e => setNewPassword(e.target.value)} />
              </FormControl>
              <Box><Button colorPalette='primary' onClick={updatePassword}>Update password</Button></Box>
            </Stack>)
          : (
            <Alert status='warning'>
              Your account password is managed by your organisation. You can't change it here.
            </Alert>)}
      </Stack>

      {user?.hasPassword
        ? (
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            <Stack rounded='lg' borderWidth='1px' p={3} gap={3}>
              <Stack direction='row' alignItems='center' gap={3}>
                <Icon as={FaMobileAlt} boxSize={6} />
                <Heading size='md' as='h3'>Authenticator app</Heading>
                <Badge colorPalette='blue'>2FA</Badge>
              </Stack>
              <Text>Use a one-time code generated by an authenticator app each time you sign in.</Text>
              {user?.otpEnabled
                ? (
                  <Stack>
                    <Alert status='success' variant='left-accent' title='Enabled'>
                      You will be asked for a temporary code generated by your 2FA app each time you login.
                    </Alert>
                    <Stack direction='row' flexWrap='wrap'>
                      <SimplePopover
                        trigger={<Button variant='outline'><Icon as={FaSync} mr={1} /> Change device</Button>}
                        header={
                          <>
                            <Heading size='md' as='h4'>Move to a new authenticator</Heading>
                            <Text>Set up your new app or device below. Your current one keeps working until the new code is verified.</Text>
                          </>
                        }
                        lazy
                        body={<AuthenticatorSetup replacing onComplete={onAuthenticatorSetUp} />}
                      />
                      <SimplePopover
                        trigger={<Button disabled={isLastFactor}>Turn off authenticator-based 2FA</Button>}
                        header={
                          <>
                            <Heading size='md' as='h4'>Turn off authenticator-based 2FA for your account</Heading>
                            <Text>We recommend keeping 2FA turned on for your account, but if you want to turn it off (or re-enable it), enter your account password below to confirm.</Text>
                          </>
                        }
                        body={
                          <Input type='password' placeholder='Your password' value={disableOtpPassword} onChange={e => setDisableOtpPassword(e.target.value)} />
                        }
                        footer={
                          <Button colorPalette='red' onClick={disableOtp}>Turn off 2FA</Button>
                        }
                      />
                    </Stack>
                    {isLastFactor &&
                      <Text fontSize='sm' color='fg.muted'>This is the only second factor on your account, so it can be changed but not turned off. Add a passkey first if you want to remove it.</Text>}
                  </Stack>)
                : (
                  <Box>
                    <SimplePopover
                      trigger={<Button colorPalette='primary'><Icon as={FaPlus} mr={1} /> Set-up 2FA</Button>}
                      header={
                        <>
                          <Heading size='md' as='h4'>Set-up 2FA</Heading>
                        </>
                      }
                      lazy
                      body={<AuthenticatorSetup onComplete={onAuthenticatorSetUp} />}
                    />
                  </Box>)}
            </Stack>

            <Stack rounded='lg' borderWidth='1px' p={3} gap={3}>
              <Stack direction='row' alignItems='center' gap={3}>
                <Icon as={FaKey} boxSize={6} />
                <Heading size='md' as='h3'>Passkeys</Heading>
                <Badge colorPalette='blue'>Login</Badge>
                <Badge colorPalette='blue'>2FA</Badge>
              </Stack>
              <Text>Sign in using your device's biometrics, screen lock, or a hardware security key. Passkeys can be used as a second factor or to sign in without a password.</Text>

              {passkeys.length > 0 &&
                <Stack gap={2}>
                  {passkeys.map(p => (
                    <Card key={p._id}>
                      <Stack direction='row' alignItems='center' gap={3}>
                        <Icon as={FaKey} />
                        <Stack gap={1}>
                          <Text fontWeight='semibold'>{p.name}</Text>
                          <Text fontSize='sm' color='fg.muted'>
                            Added {utils.formatDateTime(p.createdAt)}
                            {p.lastUsedAt ? ` · Last used ${utils.formatDateTime(p.lastUsedAt)}` : ' · Never used'}
                          </Text>
                          <Stack direction='row'>
                            <SimplePopover
                              trigger={<Button size='xs' variant='ghost' onClick={() => setRenamePasskeyName(p.name)}>Rename</Button>}
                              header={<Heading size='md' as='h4'>Rename this passkey</Heading>}
                              body={
                                <Input autoFocus placeholder='e.g. Work laptop' value={renamePasskeyName} onChange={e => setRenamePasskeyName(e.target.value)} />
                              }
                              footer={
                                <Button colorPalette='primary' onClick={() => renamePasskey(p._id)}>Save</Button>
                              }
                            />
                            <SimplePopover
                              trigger={<Button size='xs' variant='ghost' disabled={isLastFactor}>Remove passkey</Button>}
                              header={
                                <>
                                  <Heading size='md' as='h4'>Remove this passkey</Heading>
                                  <Text>Enter your account password to confirm removing "{p.name}".</Text>
                                </>
                              }
                              body={
                                <Input type='password' placeholder='Your password' value={deletePasskeyPassword} onChange={e => setDeletePasskeyPassword(e.target.value)} />
                              }
                              footer={
                                <Button colorPalette='red' onClick={() => removePasskey(p._id)}>Remove passkey</Button>
                              }
                            />
                          </Stack>
                        </Stack>
                      </Stack>
                    </Card>
                  ))}
                </Stack>}

              {isLastFactor && passkeys.length === 1 &&
                <Text fontSize='sm' color='fg.muted'>This is the only second factor on your account, so it can't be removed. Add another passkey or an authenticator app first.</Text>}

              <Box>
                <SimplePopover
                  trigger={<Button colorPalette='primary'><Icon as={FaPlus} mr={1} /> Add a passkey</Button>}
                  header={
                    <>
                      <Heading size='md' as='h4'>Add a passkey</Heading>
                      <Text>It will be named after the device or password manager that creates it. You can rename it afterwards.</Text>
                    </>
                  }
                  body={<PasskeyRegistration onComplete={onPasskeyAdded} buttonLabel='Create passkey' />}
                />
              </Box>
            </Stack>
          </SimpleGrid>)
        : (
          <Alert
            status='warning'
            title={'Your account password is managed by your organisation. You can\'t set up two-factor authentication or passkeys here.'}
          />)}

      {user?.hasPassword && hasMfa &&
        <Stack rounded='lg' borderWidth='1px' p={3} gap={3}>
          <Stack direction='row' alignItems='center' gap={3}>
            <Icon as={FaLifeRing} boxSize={6} />
            <Heading size='md' as='h3'>Recovery codes</Heading>
            <Badge colorPalette='blue'>Backup</Badge>
          </Stack>
          <Text>If you lose the device you use for two-factor authentication, you can use a recovery code to login. Please note that each code works once.</Text>

          {justRecovered &&
            <Alert status='warning' variant='left-accent' title='You signed in with a recovery code'>
              That code is now used up. If you have a new device, set it up above, then generate a fresh set of recovery codes below.
            </Alert>}

          {recovery.generatedAt
            ? (
              <Stack direction='row' gap={2} flexWrap='wrap'>
                <Badge colorPalette={recovery.remaining <= 2 ? 'red' : 'green'}>{recovery.remaining} unused</Badge>
                {recovery.used > 0 && <Badge colorPalette='orange'>{recovery.used} used</Badge>}
                <Text fontSize='sm' color='fg.muted'>Generated {utils.formatDateTime(recovery.generatedAt)}</Text>
              </Stack>)
            : (
              <Alert status='warning' variant='left-accent'>
                You don't have any recovery codes yet. Without them, losing your two factor device means that you may be locked out of your account.
              </Alert>)}

          {!justRecovered && recovery.generatedAt && recoveryNeedsAttention &&
            <Text fontSize='sm' color='fg.muted'>
              {recovery.used > 0 ? 'A code from this set has been used, so we recommend generating a new set.' : 'You are running low. We recommend generating a new set.'}
            </Text>}

          <Box>
            <SimplePopover
              trigger={<Button colorPalette={recoveryNeedsAttention ? 'primary' : undefined} variant={recoveryNeedsAttention ? undefined : 'outline'}>{recovery.generatedAt ? 'Generate new codes' : 'Generate recovery codes'}</Button>}
              header={
                <>
                  <Heading size='md' as='h4'>{recovery.generatedAt ? 'Generate a new set of recovery codes' : 'Generate recovery codes'}</Heading>
                  <Text>{recovery.generatedAt ? 'Your current codes will stop working. ' : ''}Enter your account password to confirm.</Text>
                </>
              }
              body={
                <Input type='password' autoComplete='current-password' placeholder='Your password' value={recoveryCodesPassword} onChange={e => setRecoveryCodesPassword(e.target.value)} />
              }
              footer={
                <Button colorPalette='primary' onClick={regenerateRecoveryCodes}>Generate</Button>
              }
            />
          </Box>

          <SimpleModal
            isOpen={!!newRecoveryCodes}
            onClose={() => setNewRecoveryCodes(null)}
            closeOnInteractOutside={false}
            header='Your new recovery codes'
            body={newRecoveryCodes && <RecoveryCodes codes={newRecoveryCodes} />}
            footer={<Button colorPalette='primary' onClick={() => setNewRecoveryCodes(null)}>I've saved them</Button>}
          />
        </Stack>}
    </Stack>
  )
}

export default AccountPassword
